import { BreakpointObserver } from '@angular/cdk/layout'
import {
  computed,
  DOCUMENT,
  ElementRef,
  EventEmitter,
  inject,
  Injectable,
  Output,
  signal,
  Signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { MatDrawerToggleResult, MatSidenav } from '@angular/material/sidenav'
import { map } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { PageScrollService, PageScrollTarget } from 'ngx-page-scroll-core'

/** Screen width (px) from which the sidebar pushes the page aside instead of covering it (PRENG-8242) */
export const SIDEBAR_PUSH_MIN_WIDTH = 1600

/** Sidebar width (px) until the user resizes it (PRENG-8270) */
export const SIDEBAR_DEFAULT_WIDTH = 440

/** Narrowest sidebar (px) */
export const SIDEBAR_MIN_WIDTH = 360

/** Widest sidebar, as a share of the content area (the window minus the navigation menu) */
export const SIDEBAR_MAX_CONTENT_SHARE = 0.6

/** Where the chosen width is remembered, in this browser only */
const SIDEBAR_WIDTH_STORAGE_KEY = 'wm-sidebar-width'

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  /** Event that triggers when close action is discarded because it is disabled */
  @Output() public closeDiscarded: EventEmitter<null> = new EventEmitter()

  /** If opened as obs for view usage */
  public sidebarOpened$: Observable<boolean>

  /** If closed is triggered */
  public sidebarClosedStart$: Observable<void>

  /** Pushes the page aside ('side') on wide screens, covers it with a backdrop ('over') below */
  public sideNavMode: Signal<'over' | 'side'> = toSignal(
    inject(BreakpointObserver)
      .observe(`(min-width: ${SIDEBAR_PUSH_MIN_WIDTH}px)`)
      .pipe(map(({ matches }) => (matches ? 'side' : 'over'))),
    { initialValue: 'over' }
  )

  public withBackdrop = computed(() => this.sideNavMode() === 'over')

  /** The width to render (px): the width the user chose, within the limits of the current window */
  public width = computed(() =>
    Math.round(
      Math.max(
        SIDEBAR_MIN_WIDTH,
        Math.min(this._chosenWidth(), this._maxWidth())
      )
    )
  )

  protected _pageScrollService = inject(PageScrollService)

  protected _document = inject<Document>(DOCUMENT)

  /** Instance of the mat side nav */
  private sidebar: MatSidenav

  /** Instance of the mat side nav ref */
  private sidebarRef: ElementRef

  /** Instance of container ref */
  private vcf: ViewContainerRef

  /** Whether or not we could close the sidebar  */
  private isEnableClose = true

  /** The width the user chose (px), remembered in this browser */
  private _chosenWidth = signal(this.readStoredWidth())

  /** The widest sidebar the current window allows (px) */
  private _maxWidth = signal(Number.POSITIVE_INFINITY)

  /** Sets to the sidebar
   * @param { MatSidenav } sidenav the instance of the mat sidenav
   */
  public setSidebar(sidenav: MatSidenav): void {
    this.sidebar = sidenav
    this.sidebarOpened$ = this.sidebar.openedChange.asObservable()

    this.sidebarClosedStart$ = this.sidebar.closedStart
  }

  /** Sets to the sidebar ref
   * @param { ElementRef } sidenavRef the instance of the mat sidenav ref
   */
  public setSidebarRef(sidenavRef: ElementRef): void {
    this.sidebarRef = sidenavRef
  }

  /** Sets to the container
   * @param { ViewContainerRef } viewContainerRef the instance of container ref
   */
  public setContentVcf(viewContainerRef: ViewContainerRef): void {
    this.vcf = viewContainerRef
  }

  /** Create an anchor of the embedded view
   * @param { TemplateRef } template ref to copy
   */
  public createView<T>(template: TemplateRef<T>): void {
    this.vcf.clear()
    this.vcf.createEmbeddedView(template)
  }

  /** Opens the sidebar
   * @param { TemplateRef } template ref to copy
   */
  public async open<T>(
    template: TemplateRef<T>
  ): Promise<MatDrawerToggleResult> {
    this.createView(template)
    return this.sidebar.open()
  }

  /** Closes the sidebar */
  public async close(): Promise<MatDrawerToggleResult | void> {
    if (this.isEnableClose) {
      this.vcf.clear()
      return this.sidebar.close()
    }

    this.closeDiscarded.emit()
  }

  /**
   * Whether Escape should close the sidebar, wherever the focus is (PRENG-8242): it's open, nothing on top (a menu,
   * a select, a picker) already handled the key, and no dialog is open over it
   * @param { KeyboardEvent } event the Escape keydown
   */
  public closesOnEscape(event: KeyboardEvent): boolean {
    return (
      !!this.sidebar?.opened &&
      !event.defaultPrevented &&
      !this._document.querySelector(
        '.cdk-overlay-container .mat-mdc-dialog-container'
      )
    )
  }

  /**
   * Sets the widest sidebar from the content area (the window minus the navigation menu)
   * @param { number } contentAreaWidth the content area's width (px)
   */
  public setContentAreaWidth(contentAreaWidth: number): void {
    this._maxWidth.set(contentAreaWidth * SIDEBAR_MAX_CONTENT_SHARE)
  }

  /**
   * Resizes the sidebar within its limits and remembers the width in this browser
   * @param { number } width the wanted width (px)
   */
  public resize(width: number): void {
    const allowed = Math.round(
      Math.max(SIDEBAR_MIN_WIDTH, Math.min(width, this._maxWidth()))
    )

    this._chosenWidth.set(allowed)
    this.storeWidth(allowed)
  }

  /** Puts the sidebar back to its default width */
  public resetWidth(): void {
    this.resize(SIDEBAR_DEFAULT_WIDTH)
  }

  /** Opens the sidebar */
  public async toggle(): Promise<MatDrawerToggleResult> {
    return this.sidebar.toggle()
  }

  /** Authorized or not the close sidebar action */
  public enableClose(val: boolean): void {
    this.isEnableClose = val
  }

  /** Used to scroll in the sidebar to the target
   * @param { PageScrollTarget } scrollTarget the target to reach
   */
  public scrollTo(scrollTarget: PageScrollTarget): void {
    const viewTarget = this.sidebarRef.nativeElement.children[0]

    if (viewTarget) {
      const scrollInstance = this._pageScrollService.create({
        document: this._document,
        scrollTarget,
        scrollViews: [viewTarget],
      })

      this._pageScrollService.start(scrollInstance)
    }
  }

  /** The width remembered in this browser, or the default one (storage can be blocked) */
  private readStoredWidth(): number {
    try {
      const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY))

      return stored > 0 ? stored : SIDEBAR_DEFAULT_WIDTH
    } catch {
      return SIDEBAR_DEFAULT_WIDTH
    }
  }

  /**
   * Remembers the width in this browser, when storage is allowed
   * @param { number } width the width to remember (px)
   */
  private storeWidth(width: number): void {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(width))
    } catch {
      // storage blocked: the width only lasts until the page reloads
    }
  }
}
